/*
 * ИИ-агент над данными Атырау — клиент стороннего сервиса MAI.
 *
 * Сервис с 01.10.2026 на spark (192.168.2.57:8200), доступен под префиксом
 * `/ai`: в dev его ведёт прокси Vite, в образе — nginx контейнера. Тот же
 * контракт, что у веб-витрины (`sc-frontend-atyrau/src/api/aiAnalysis/
 * chatAgentApi.ts`), — правки протокола переносить в оба.
 *
 * ДВА РЕЖИМА. Пять доменов (`otinish`, `population`, `epvo`, `control`,
 * `procurement`) — агент сам ходит в базу и считает. `page` — у раздела нет
 * своих таблиц у сервиса, и всё, чем агент располагает, приезжает в `context`
 * от экрана: его числа, разрезы, оговорки.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ КЛИЕНТ, А НЕ `api.ts`. Наш `apiRequest` подставляет Bearer
 * бэкенда Атырау в каждый запрос. Отдать действующий токен чужому сервису,
 * который сам работает БЕЗ авторизации, — утечка. Плюс он обновляет токен по
 * 401, а на чужом хосте 401 означает другое, и лишняя ротация одноразового
 * refresh-токена разлогинила бы пользователя.
 *
 * ПОЧЕМУ НЕ `EventSource`. Он умеет только GET и не может отправить тело, а
 * запрос требует JSON с доменом и текстом.
 *
 * ОТВЕЧАЕТ ДОЛГО: 5–60 секунд, предел 180. Это работа модели на видеокарте,
 * которую она делит с фоновой классификацией, а не зависание. Таймаут ниже
 * 300 секунд ставить нельзя, а ожидание обязано быть видимым — для этого и
 * нужны события `step`.
 */

const BASE = (import.meta.env.VITE_AI_ANALYSIS_BASE_URL ?? "").replace(/\/$/, "");

export function aiAgentConfigured(): boolean {
  return BASE.length > 0;
}

export type AiAgentDomain =
  | "otinish"
  | "population"
  | "epvo"
  | "control"
  | "procurement"
  | "page";

/*
 * `control` (сроки и решения органов по реестру е-Өтініш) и `procurement`
 * (цены в планах госзакупок) — ОБЩЕРЕСПУБЛИКАНСКИЕ. Без `region` агент
 * отвечает по всей стране, и «какие органы нарушают сроки» вернёт департаменты
 * Алматы, а на панели акима Атырау это прочтут как свои. `23` — Атырауская
 * область и как `organ_region_code`, и как префикс КАТО. Другим доменам
 * `region` слать нельзя — 422.
 */
const REGIONAL_DOMAINS: ReadonlySet<AiAgentDomain> = new Set(["control", "procurement"]);
export const ATYRAU_REGION_CODE = "23";

/** Откуда числа экрана. `mock` обязывает агента говорить это в каждом ответе. */
export type PageProvenanceKind = "live" | "snapshot" | "mock";

export interface PageMetric {
  key: string;
  label: string;
  value: number;
  unit?: string;
  /**
   * Число так, как оно написано на экране: «7 514», «≈ 5,4». Агент цитирует
   * именно его — иначе в ответе «7514» рядом с плиткой «7 514».
   */
  display?: string;
  hint?: string;
}

export interface PageBreakdown {
  key: string;
  label: string;
  dimension: string;
  unit?: string;
  total?: number;
  /** Строки — голова длинного списка: складывать их и звать итогом нельзя. */
  truncated?: boolean;
  rows: { label: string; value: number; share?: number }[];
}

export interface PageSeries {
  key: string;
  label: string;
  unit?: string;
  /** `t` — подпись оси («авг 26»), а не дата: её никто не разбирает. */
  points: { t: string; value: number }[];
}

export interface PageTable {
  key: string;
  label: string;
  columns: string[];
  rows: (number | string | null)[][];
  /** Полная длина списка, если она больше `rows`. */
  total_rows?: number;
}

export interface PageContext {
  context_version: 1;
  page: { id: string; title: string; url?: string; locale?: "ru" | "kk" | "en" };
  provenance: {
    kind: PageProvenanceKind;
    sources?: string[];
    /** `YYYY-MM-DD`. */
    as_of?: string;
    period?: { from?: string; to?: string; label?: string };
  };
  metrics?: PageMetric[];
  breakdowns?: PageBreakdown[];
  series?: PageSeries[];
  /** Срез, которому принадлежит каждая цифра: «Район: Макатский». */
  filters?: { label: string; value: string }[];
  /** Одна запись: сводка, где значения — уже отформатированные строки. */
  entity?: {
    kind: string;
    title: string;
    fields: { label: string; value: number | string; unit?: string }[];
  };
  tables?: PageTable[];
  /** Оговорки, уже напечатанные на экране. */
  notes?: string[];
  /** Чего в данных экрана нет — чтобы агент не выдумывал. */
  cannot_answer?: string[];
}

/** Один вызов инструмента: что агент спросил у базы и сколько это заняло. */
export interface AiAgentStep {
  tool: string;
  /** У `run_sql` здесь же лежит сам запрос (`sql`) и зачем он (`reason`). */
  args?: Record<string, unknown>;
  elapsed_ms?: number;
  status?: "requested";
  /** Что инструмент вернул, строкой JSON. */
  result?: string;
}

export type AiAgentEvent =
  | { event: "conversation"; data: { conversation_id: string; domain: AiAgentDomain } }
  | { event: "step"; data: AiAgentStep }
  | { event: "token"; data: { text: string } }
  | { event: "answer"; data: { text: string; steps: AiAgentStep[]; elapsed_ms: number } }
  | { event: "error"; data: { message: string } }
  | { event: "done"; data: { elapsed_ms: number } };

export interface AiAgentRequest {
  domain: AiAgentDomain;
  message: string;
  conversation_id?: string | null;
  user_id?: string | null;
  /** Обязателен при `domain: "page"`; с остальными доменами — 422. */
  context?: PageContext | null;
  /** Только `control` и `procurement`; подставляется сам, см. `REGIONAL_DOMAINS`. */
  region?: string | null;
}

/*
 * Пределы схемы контекста на стороне сервиса. Превышение — 422 на весь
 * вопрос, а не обрезка: урезанный молча контекст дал бы правдоподобный
 * неверный ответ, поэтому режем здесь и помечаем, что отрезано.
 */
const MAX_METRICS = 40;
const MAX_BREAKDOWNS = 12;
const MAX_BREAKDOWN_ROWS = 30;
const MAX_SERIES = 5;
const MAX_SERIES_POINTS = 120;
const MAX_NOTES = 10;
const MAX_FILTERS = 12;
const MAX_ENTITY_FIELDS = 40;
const MAX_TABLES = 3;
const MAX_TABLE_COLUMNS = 12;
const MAX_TABLE_ROWS = 50;
const MAX_STRING = 500;
const KEY_MAX = 64;

/*
 * Ключ — `^[a-z][a-z0-9_]{0,63}$`, и ОДИН неверный ключ — 422 на весь вопрос
 * (на веб-витрине так падала вся страница групп: `population.register`).
 * Ключи собирают модули из своих id, следить за шаблоном в каждом нельзя.
 */
function contextKey(raw: string): string {
  let key = raw.toLowerCase().replace(/[^a-z0-9_]+/g, "_");
  if (!/^[a-z]/.test(key)) key = `k_${key}`;
  return key.slice(0, KEY_MAX);
}

/* Любая строка — до 500 знаков, иначе 422. Названия органов бывают длиннее. */
const clip = (s: string) => (s.length > MAX_STRING ? `${s.slice(0, MAX_STRING - 1)}…` : s);

/**
 * Привести контекст к пределам схемы ПЕРЕД отправкой — здесь, а не в каждом
 * модуле: размер разрезов задают справочники источника (районы, статьи КоАП),
 * и экран, который никто не трогал, иначе однажды начнёт отвечать 422.
 *
 * Обрезанная разбивка помечается `truncated`, у таблицы остаётся полная длина
 * в `total_rows`: без пометок агент сложит оставшееся и назовёт это итогом.
 */
export function clampPageContext(ctx: PageContext): PageContext {
  const notes = (ctx.notes ?? []).map(clip);
  const addNote = (text: string) => {
    if (notes.length < MAX_NOTES) notes.push(text);
  };

  const breakdowns = (ctx.breakdowns ?? []).slice(0, MAX_BREAKDOWNS).map((b) => ({
    ...b,
    key: contextKey(b.key),
    label: clip(b.label),
    truncated: b.truncated || b.rows.length > MAX_BREAKDOWN_ROWS,
    rows: b.rows.slice(0, MAX_BREAKDOWN_ROWS).map((r) => ({ ...r, label: clip(r.label) })),
  }));
  if ((ctx.breakdowns?.length ?? 0) > MAX_BREAKDOWNS) {
    addNote("Часть разрезов экрана в этот срез не поместилась.");
  }

  // У ряда остаются ПОСЛЕДНИЕ точки: вопрос к ряду почти всегда про свежий конец.
  const series = (ctx.series ?? []).slice(0, MAX_SERIES).map((s) => {
    const points = s.points.slice(-MAX_SERIES_POINTS);
    if (points.length < s.points.length) {
      addNote(`Ряд «${s.label}» показан с ${points[0].t}: ранние точки не поместились.`);
    }
    return { ...s, key: contextKey(s.key), points };
  });

  // Пустой фильтр — 422 (`min_length=1`), а невыбранный и так ничего не сужает.
  const filters = (ctx.filters ?? [])
    .filter((f) => f.label.trim() && f.value.trim())
    .slice(0, MAX_FILTERS)
    .map((f) => ({ label: clip(f.label), value: clip(f.value) }));

  const entity =
    ctx.entity && ctx.entity.fields.length
      ? {
          ...ctx.entity,
          fields: ctx.entity.fields.slice(0, MAX_ENTITY_FIELDS).map((f) => ({
            ...f,
            value: typeof f.value === "string" ? clip(f.value) : f.value,
          })),
        }
      : undefined;

  // Пустая строка в ячейке — тоже 422, поэтому она едет как `null`.
  const tables = (ctx.tables ?? []).slice(0, MAX_TABLES).map((tb) => {
    const width = Math.min(tb.columns.length, MAX_TABLE_COLUMNS);
    return {
      ...tb,
      key: contextKey(tb.key),
      columns: tb.columns.slice(0, width),
      rows: tb.rows
        .slice(0, MAX_TABLE_ROWS)
        .map((row) =>
          row
            .slice(0, width)
            .map((cell) => (cell === "" ? null : typeof cell === "string" ? clip(cell) : cell)),
        ),
      total_rows:
        tb.rows.length > MAX_TABLE_ROWS
          ? Math.max(tb.total_rows ?? 0, tb.rows.length)
          : tb.total_rows,
    };
  });

  return {
    ...ctx,
    metrics: (ctx.metrics ?? [])
      .filter((m) => Number.isFinite(m.value))
      .slice(0, MAX_METRICS)
      .map((m) => ({ ...m, key: contextKey(m.key), label: clip(m.label) })),
    breakdowns,
    series,
    ...(filters.length ? { filters } : {}),
    ...(entity ? { entity } : {}),
    ...(tables.length ? { tables } : {}),
    notes,
    cannot_answer: (ctx.cannot_answer ?? []).slice(0, MAX_NOTES).map(clip),
  };
}

/**
 * Поток ответа агента.
 *
 * ПОРЯДОК: `conversation` первым, затем `step` и `token` вперемешку, затем
 * ЛИБО `answer`, ЛИБО `error` — никогда оба, — и всегда `done` в конце.
 */
export async function* streamAiAgent(
  req: AiAgentRequest,
  signal?: AbortSignal,
): AsyncGenerator<AiAgentEvent> {
  const body: AiAgentRequest = {
    ...req,
    context: req.context ? clampPageContext(req.context) : undefined,
    region: REGIONAL_DOMAINS.has(req.domain) ? (req.region ?? ATYRAU_REGION_CODE) : undefined,
  };
  const res = await fetch(`${BASE}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "omit",
    signal,
  });

  // Ошибки валидации приходят обычным JSON с не-2xx, а не потоком.
  if (!res.ok || !res.body) {
    throw new Error(`ai-agent ${res.status}: ${await res.text()}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Событие SSE заканчивается пустой строкой; хвост остаётся в буфере до
    // следующего чтения — иначе половина JSON улетала бы в разбор.
    let split: number;
    while ((split = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);

      let name = "message";
      let payload = "";
      for (const line of block.split("\n")) {
        if (line.startsWith("event: ")) name = line.slice(7);
        else if (line.startsWith("data: ")) payload += line.slice(6);
      }
      if (payload) yield { event: name, data: JSON.parse(payload) } as AiAgentEvent;
    }
  }
}

/**
 * Человеческая подпись к шагу агента.
 *
 * Без неё ожидание в полминуты выглядит зависанием: видна крутилка, и неясно,
 * идёт ли работа. С подписью видно, что именно считается.
 */
const STEP_LABEL: Record<string, string> = {
  population_vital: "Считаю рождения, смерти и браки",
  population_structure: "Смотрю структуру населения",
  population_dimensions: "Уточняю справочник измерений",
  population_regions: "Проверяю список районов",
  otinish_coverage: "Проверяю охват классификации",
  otinish_stats: "Считаю обращения",
  otinish_volume: "Считаю объём обращений по периодам",
  otinish_search: "Ищу обращения",
  otinish_appeal: "Открываю обращение",
  otinish_org_search: "Ищу организацию",
  otinish_problems: "Ищу системные проблемы",
  otinish_citizen_repeats: "Ищу повторные обращения одного заявителя",
  control_outcomes: "Считаю решения и сроки по органам",
  control_repeaters: "Ищу повторные обращения по темам",
  control_topic_spikes: "Ищу всплески тем по неделям",
  procurement_item_search: "Ищу позицию в справочнике закупок",
  procurement_price_outliers: "Сравниваю цены с медианой",
  epvo_coverage: "Проверяю состав данных",
  epvo_student_stats: "Считаю студентов",
  epvo_reference: "Смотрю справочник",
  run_sql: "Выполняю запрос к базе",
};

export function aiAgentStepLabel(tool: string): string {
  return STEP_LABEL[tool] ?? `Шаг: ${tool}`;
}

export interface AiAgentAnswer {
  text: string;
  /** Журнал вызовов — «откуда это число». В режиме `page` пуст: считать нечего. */
  steps: AiAgentStep[];
  conversationId: string | null;
}

/**
 * Спросить агента и дождаться проверенного ответа.
 *
 * ВОЗВРАЩАЕТ `answer.text`, А НЕ НАКОПЛЕННЫЕ ТОКЕНЫ. Токены годятся на эффект
 * набора; итоговый текст проверен, и в урезанном ответе (агент исчерпал восемь
 * шагов) верен только он. Токены отдаём в `onToken` — пусть их рисует тот, кому
 * нужен эффект.
 */
export async function askAiAgent(
  req: AiAgentRequest,
  handlers: {
    onStep?: (label: string) => void;
    onToken?: (text: string) => void;
    signal?: AbortSignal;
  } = {},
): Promise<AiAgentAnswer> {
  let text = "";
  let draft = "";
  let steps: AiAgentStep[] = [];
  let conversationId: string | null = null;
  let failure: string | null = null;
  let lastLabel = "";

  for await (const ev of streamAiAgent(req, handlers.signal)) {
    switch (ev.event) {
      case "conversation":
        conversationId = ev.data.conversation_id;
        break;
      case "step": {
        /*
         * Подпись — на «запросил», а не на «вернулось»: иначе человек узнаёт,
         * что агент лез в базу, когда тот уже вышел, и первые полминуты самого
         * долгого запроса подписи нет. Двоение снимается сравнением с прошлой.
         */
        const label = aiAgentStepLabel(ev.data.tool);
        if (label !== lastLabel) handlers.onStep?.(label);
        lastLabel = label;
        break;
      }
      case "token":
        draft += ev.data.text;
        handlers.onToken?.(ev.data.text);
        break;
      case "answer":
        text = ev.data.text;
        steps = ev.data.steps ?? [];
        break;
      case "error":
        failure = ev.data.message;
        break;
    }
  }

  if (failure) throw new Error(failure);
  // Поток закрылся без текста — под нагрузкой бывает. Это не ответ.
  if (!text && !draft) throw new Error("агент закрыл поток без текста");
  return { text: text || draft, steps, conversationId };
}
